import { useCallback, useEffect, useState } from "react";

import type {
  ListSchemaTelemetryOptions,
  ListSchemaTelemetryResponse,
  SchemaTelemetryRecord,
} from "../types/schemaTelemetry";

import {
  exportSchemaTelemetry,
  fetchSchemaTelemetryDetail,
  fetchSchemaTelemetryList,
} from "../api/schemaTelemetry";

export function useSchemaTelemetry() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ListSchemaTelemetryResponse>({
    rows: [],
    total: 0,
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SchemaTelemetryRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async (options: ListSchemaTelemetryOptions) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSchemaTelemetryList(options);
      setData(result);
    } catch (_error) {
      setError(_error instanceof Error ? _error.message : String(_error));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const result = await fetchSchemaTelemetryDetail(id);
      setDetail(result);
    } catch (_error) {
      setError(_error instanceof Error ? _error.message : String(_error));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      void fetchDetail(selectedId);
    } else {
      setDetail(null);
    }
  }, [selectedId, fetchDetail]);

  const handleExport = useCallback(async (ids: number[]) => {
    try {
      await exportSchemaTelemetry(ids);
    } catch (_error) {
      setError(_error instanceof Error ? _error.message : String(_error));
    }
  }, []);

  return {
    data,
    detail,
    detailLoading,
    error,
    fetchList,
    handleExport,
    loading,
    selectedId,
    setSelectedId,
  };
}
